/** @fileoverview Reads a runtime route's answer as JSON or as a file, and saves a file in the browser. */

/** A file a runtime route sent back instead of JSON, such as an action's Excel export. */
export type RuntimeFile = { blob: Blob; fileName: string };

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Tells whether a runtime answer is a file. Only `readRuntimeResponse` makes one, from the
 * headers, and parsed JSON never holds a Blob, so a JSON body is never taken for a file.
 * @param value the runtime answer
 */
export const isRuntimeFile = (value: unknown): value is RuntimeFile =>
  typeof value === 'object' && value !== null && (value as { blob?: unknown }).blob instanceof Blob;

/**
 * Decodes a percent-encoded file name, or returns it as is when it is not valid encoding.
 * @param value the encoded name
 */
const decodeFileName = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Reads the file name from a Content-Disposition header: `filename*` first, then `filename`.
 * Returns '' when the header has no name.
 * @param header the Content-Disposition header value
 */
export const fileNameFromContentDisposition = (header: string | null): string => {
  if (!header) {
    return '';
  }
  const encodedMatch = /filename\*\s*=\s*([^;]+)/i.exec(header);
  if (encodedMatch) {
    const encoded = encodedMatch[1].trim().replace(/^"(.*)"$/, '$1');
    const quoteIndex = encoded.indexOf("''");
    const name = decodeFileName(quoteIndex >= 0 ? encoded.slice(quoteIndex + 2) : encoded).trim();
    if (name) {
      return name;
    }
  }
  const plainMatch = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
  return (plainMatch?.[2] ?? plainMatch?.[1] ?? '').trim();
};

/**
 * Reads a successful answer: JSON when the server sent JSON, nothing for 204, a file when the
 * server marked it an attachment or sent an Excel file, else an error (such as a proxy's HTML page).
 * The file name falls back to `export.xlsx` for an Excel file and `download` for anything else.
 * @param response the successful fetch response
 */
export const readRuntimeResponse = async (response: Response): Promise<unknown> => {
  if (response.status === 204) {
    return undefined;
  }
  const contentType = response.headers.get('Content-Type') ?? '';
  if (/[/+]json\b/i.test(contentType)) {
    return response.json();
  }
  const disposition = response.headers.get('Content-Disposition');
  const isXlsx = contentType.startsWith(XLSX_CONTENT_TYPE);
  if (!isXlsx && !/attachment/i.test(disposition ?? '')) {
    throw new Error(
      `The server sent an unexpected answer (${
        contentType || 'no Content-Type'
      }) instead of JSON or a file.`
    );
  }
  const blob = await response.blob();
  const fileName =
    fileNameFromContentDisposition(disposition) || (isXlsx ? 'export.xlsx' : 'download');
  return { blob, fileName } satisfies RuntimeFile;
};

/**
 * Saves a file through a temporary link, then frees the link's object URL.
 * @param file the file the server sent back
 */
export const saveRuntimeFile = ({ blob, fileName }: RuntimeFile): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  /* Freed on the next tick, so the browser has started the download first. */
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
